import { faqs, experiences, reviewStats, t } from "@/content/site";

export const SITE_URL = "https://vilamouradreamscruises.com";

export const sharedOG = {
  image: `${SITE_URL}/__l5e/assets-v1/e0b0ae03-118a-4c8f-ad5a-c196cbf5f5de/hero-poster.jpg`,
};

const BUILD_DATE = new Date().toISOString().split("T")[0];

/** VideoObject schema for the hero YouTube — boosts video carousel / rich results. */
export function heroVideoLD() {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "Morningstar Yacht — Vilamoura, Algarve",
    description:
      "Private yacht charters from Marina de Vilamoura aboard Morningstar — Benagil Cave tours, sunset cruises and Algarve day trips.",
    thumbnailUrl: [`${SITE_URL}/__l5e/assets-v1/e0b0ae03-118a-4c8f-ad5a-c196cbf5f5de/hero-poster.jpg`],
    uploadDate: "2024-05-01",
    contentUrl: `${SITE_URL}/__l5e/assets-v1/88f8a29d-06aa-4524-b5c9-e906ef481c5b/hero.mp4`,
    embedUrl: `${SITE_URL}/__l5e/assets-v1/88f8a29d-06aa-4524-b5c9-e906ef481c5b/hero.mp4`,
    publisher: { "@id": `${SITE_URL}/#business` },
  };
}

/** Individual Review entries (in addition to AggregateRating) — richer SERP previews. */
function individualReviewsLD() {
  return [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Jim V" },
      datePublished: "2026-02-15",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "First class. Impressive organisation. Incredible trip. Wonderful hosts. A memorable day that will be remembered as one of the best ever. I highly recommend.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Michael M" },
      datePublished: "2025-10-12",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "The highlight of our trip to Portugal. The crew was friendly and informed. We swam in refreshing water, then a beautiful spread was prepared on board. Absolutely delicious. An experience not to miss.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Pamela B" },
      datePublished: "2025-08-20",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "We all had a wonderful afternoon. The crew made us feel welcome — everything was first class. I highly recommend booking a trip with this company.",
    },
  ];
}

/** Reusable LocalBusiness (TravelAgency) block with real aggregate rating. */
export function localBusinessLD() {
  return {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "@id": `${SITE_URL}/#business`,
    name: "Morningstar Yacht — Vilamoura Dreams Cruises",
    description:
      "Private yacht charters from Marina de Vilamoura aboard Morningstar — Benagil Cave tours, sunset cruises, Algarve day trips.",
    url: SITE_URL,
    image: sharedOG.image,
    telephone: "+351939418375",
    priceRange: "€€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Marina de Vilamoura, Gate A2",
      addressLocality: "Vilamoura",
      postalCode: "8125-409",
      addressRegion: "Algarve",
      addressCountry: "PT",
    },
    geo: { "@type": "GeoCoordinates", latitude: 37.073, longitude: -8.118 },
    areaServed: { "@type": "Place", name: "Algarve, Portugal" },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      bestRating: "5",
      reviewCount: reviewStats.google.count + reviewStats.tripadvisor.count,
    },
    review: individualReviewsLD(),
    sameAs: [reviewStats.google.url, reviewStats.tripadvisor.url],
  };
}

/** Build common head() output for any landing/home route. */
export function buildHead(opts: {
  lang: "en" | "pt";
  path: string; // e.g. "/benagil-cave-tour" or "/" or "/pt"
  title: string;
  description: string;
  keywords?: string;
  altPath: { en: string; pt: string }; // hreflang pair
  ogImage?: string;
  extraScripts?: Array<{ type: "application/ld+json"; children: string }>;
}) {
  const url = `${SITE_URL}${opts.path}`;
  const img = opts.ogImage ?? sharedOG.image;
  const meta: Array<Record<string, string>> = [
    { title: opts.title },
    { name: "description", content: opts.description },
    ...(opts.keywords ? [{ name: "keywords", content: opts.keywords }] : []),
    { property: "og:title", content: opts.title },
    { property: "og:description", content: opts.description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    { property: "og:image", content: img },
    { property: "og:locale", content: opts.lang === "pt" ? "pt_PT" : "en_GB" },
    { property: "og:locale:alternate", content: opts.lang === "pt" ? "en_GB" : "pt_PT" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: opts.description },
    { name: "twitter:image", content: img },
  ];
  const links: Array<Record<string, string>> = [
    { rel: "canonical", href: url },
    { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${opts.altPath.en}` },
    { rel: "alternate", hrefLang: "pt", href: `${SITE_URL}${opts.altPath.pt}` },
    { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${opts.altPath.en}` },
  ];
  const scripts = [
    { type: "application/ld+json" as const, children: JSON.stringify(localBusinessLD()) },
    { type: "application/ld+json" as const, children: JSON.stringify(heroVideoLD()) },
    ...(opts.extraScripts ?? []),
  ];
  return { meta, links, scripts };
}

export function breadcrumbLD(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function touristTripLD(opts: {
  name: string;
  description: string;
  url: string;
  image?: string;
  itinerary?: string;
  durationHours?: number;
  priceFromEUR?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    image: opts.image ?? sharedOG.image,
    touristType: ["Couples", "Families", "Groups", "Celebrations"],
    itinerary: opts.itinerary,
    duration: opts.durationHours ? `PT${opts.durationHours}H` : undefined,
    dateModified: BUILD_DATE,
    provider: { "@id": `${SITE_URL}/#business` },
    offers: {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      price: opts.priceFromEUR,
      priceSpecification: opts.priceFromEUR
        ? {
            "@type": "PriceSpecification",
            priceCurrency: "EUR",
            price: opts.priceFromEUR,
            valueAddedTaxIncluded: true,
            description: "Per charter, up to 18 guests",
          }
        : undefined,
      url: opts.url,
    },
  };
}

export function faqLD(lang: "en" | "pt") {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["#faq"],
    },
    mainEntity: faqs[lang].map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function homeOfferCatalogLD(lang: "en" | "pt") {
  return {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name:
      lang === "pt"
        ? "Morningstar Yacht — Charters Privados em Vilamoura"
        : "Morningstar Yacht — Vilamoura Private Charters",
    description: t[lang].seo.description,
    areaServed: { "@type": "Place", name: "Algarve, Portugal" },
    makesOffer: experiences.map((e) => ({
      "@type": "Offer",
      name: e.title[lang],
      description: e.description[lang],
      itemOffered: {
        "@type": "Service",
        name: e.title[lang],
        serviceType: lang === "pt" ? "Charter de iate privado" : "Private yacht charter",
        provider: { "@id": `${SITE_URL}/#business` },
        areaServed: { "@type": "Place", name: "Algarve, Portugal" },
      },
    })),
  };
}

/* ----------------------------------------------------------------------------
 * Route metadata map — drives visible breadcrumbs and "related cruises" blocks
 * inside SitePage, so we don't have to touch every leaf route file.
 * -------------------------------------------------------------------------- */

export interface RouteMeta {
  lang: "en" | "pt";
  name: string;
  parentLabel: string; // "Cruises" / "Cruzeiros"
}

export const ROUTE_META: Record<string, RouteMeta> = {
  "/benagil-cave-tour": { lang: "en", name: "Benagil Cave Tour", parentLabel: "Cruises" },
  "/vilamoura-boat-trips": { lang: "en", name: "Vilamoura Boat Trips", parentLabel: "Cruises" },
  "/sunset-cruise-vilamoura": { lang: "en", name: "Sunset Cruise", parentLabel: "Cruises" },
  "/private-yacht-charter-vilamoura": { lang: "en", name: "Private Yacht Charter", parentLabel: "Cruises" },
  "/boat-hire-vilamoura": { lang: "en", name: "Boat Hire Vilamoura", parentLabel: "Cruises" },
  "/private-boat-rental-vilamoura": { lang: "en", name: "Private Boat Rental Vilamoura", parentLabel: "Cruises" },
  "/the-morningstar": { lang: "en", name: "The Morningstar", parentLabel: "Yacht" },
  "/dolphin-watching-vilamoura": { lang: "en", name: "Dolphin Watching", parentLabel: "Cruises" },
  "/ria-formosa-tavira-cruise": { lang: "en", name: "Ria Formosa & Tavira Cruise", parentLabel: "Cruises" },
  "/alvor-lagoon-cruise": { lang: "en", name: "Alvor Lagoon Cruise", parentLabel: "Cruises" },
  "/vilamoura-hen-party-cruise": { lang: "en", name: "Hen Party Cruise", parentLabel: "Occasions" },
  "/birthday-cruise-vilamoura": { lang: "en", name: "Birthday Cruise", parentLabel: "Occasions" },
  "/proposal-cruise-vilamoura": { lang: "en", name: "Proposal Cruise", parentLabel: "Occasions" },
  "/boat-trips-quarteira": { lang: "en", name: "Boat Trips from Quarteira", parentLabel: "Cruises" },
  "/boat-trips-albufeira": { lang: "en", name: "Boat Trips from Albufeira", parentLabel: "Cruises" },
  "/boat-trips-faro": { lang: "en", name: "Boat Trips from Faro", parentLabel: "Cruises" },
  "/algarve-boat": { lang: "en", name: "Algarve Boat Charters", parentLabel: "Cruises" },
  "/boat-hire-algarve": { lang: "en", name: "Boat Hire Algarve", parentLabel: "Cruises" },

  "/pt/gruta-de-benagil": { lang: "pt", name: "Gruta de Benagil", parentLabel: "Cruzeiros" },
  "/pt/passeios-de-barco-vilamoura": { lang: "pt", name: "Passeios de Barco Vilamoura", parentLabel: "Cruzeiros" },
  "/pt/cruzeiro-por-do-sol-vilamoura": { lang: "pt", name: "Cruzeiro Pôr do Sol", parentLabel: "Cruzeiros" },
  "/pt/charter-iate-privado-vilamoura": { lang: "pt", name: "Charter de Iate Privado", parentLabel: "Cruzeiros" },
  "/pt/aluguer-de-barco-vilamoura": { lang: "pt", name: "Aluguer de Barco Vilamoura", parentLabel: "Cruzeiros" },
  "/pt/o-morningstar": { lang: "pt", name: "O Morningstar", parentLabel: "Iate" },
  "/pt/observacao-de-golfinhos-vilamoura": { lang: "pt", name: "Observação de Golfinhos", parentLabel: "Cruzeiros" },
  "/pt/cruzeiro-ria-formosa-tavira": { lang: "pt", name: "Cruzeiro Ria Formosa & Tavira", parentLabel: "Cruzeiros" },
  "/pt/cruzeiro-laguna-alvor": { lang: "pt", name: "Cruzeiro Laguna de Alvor", parentLabel: "Cruzeiros" },
  "/pt/cruzeiro-despedida-solteira-vilamoura": { lang: "pt", name: "Cruzeiro Despedida de Solteira", parentLabel: "Ocasiões" },
  "/pt/cruzeiro-aniversario-vilamoura": { lang: "pt", name: "Cruzeiro de Aniversário", parentLabel: "Ocasiões" },
  "/pt/cruzeiro-pedido-casamento-vilamoura": { lang: "pt", name: "Cruzeiro Pedido de Casamento", parentLabel: "Ocasiões" },
  "/pt/passeios-de-barco-quarteira": { lang: "pt", name: "Passeios de Barco Quarteira", parentLabel: "Cruzeiros" },
  "/pt/passeios-de-barco-albufeira": { lang: "pt", name: "Passeios de Barco Albufeira", parentLabel: "Cruzeiros" },
  "/pt/passeios-de-barco-faro": { lang: "pt", name: "Passeios de Barco Faro", parentLabel: "Cruzeiros" },
  "/pt/barco-algarve": { lang: "pt", name: "Barco no Algarve", parentLabel: "Cruzeiros" },
  "/pt/aluguer-de-barco-algarve": { lang: "pt", name: "Aluguer de Barco Algarve", parentLabel: "Cruzeiros" },
};

/** Return up to N related routes from the same language, excluding the current one. */
export function getRelatedRoutes(currentPath: string, n = 4): Array<{ path: string; name: string }> {
  const current = ROUTE_META[currentPath];
  if (!current) return [];
  const pool = Object.entries(ROUTE_META).filter(
    ([p, m]) => p !== currentPath && m.lang === current.lang,
  );
  // Shuffle deterministically per current path so SSR + client match.
  const seed = currentPath.length;
  const shuffled = [...pool].sort((a, b) => {
    const ah = (a[0].charCodeAt(0) + seed) % 13;
    const bh = (b[0].charCodeAt(0) + seed) % 13;
    return ah - bh;
  });
  return shuffled.slice(0, n).map(([path, m]) => ({ path, name: m.name }));
}

/** EN <-> PT path pairs for the language switcher. */
export const ROUTE_ALT_PAIRS: Array<[string, string]> = [
  ["/", "/pt"],
  ["/faq", "/pt/faq"],
  ["/benagil-cave-tour", "/pt/gruta-de-benagil"],
  ["/vilamoura-boat-trips", "/pt/passeios-de-barco-vilamoura"],
  ["/sunset-cruise-vilamoura", "/pt/cruzeiro-por-do-sol-vilamoura"],
  ["/private-yacht-charter-vilamoura", "/pt/charter-iate-privado-vilamoura"],
  ["/boat-hire-vilamoura", "/pt/aluguer-de-barco-vilamoura"],
  ["/private-boat-rental-vilamoura", "/pt/aluguer-de-barco-vilamoura"],
  ["/the-morningstar", "/pt/o-morningstar"],
  ["/dolphin-watching-vilamoura", "/pt/observacao-de-golfinhos-vilamoura"],
  ["/ria-formosa-tavira-cruise", "/pt/cruzeiro-ria-formosa-tavira"],
  ["/alvor-lagoon-cruise", "/pt/cruzeiro-laguna-alvor"],
  ["/vilamoura-hen-party-cruise", "/pt/cruzeiro-despedida-solteira-vilamoura"],
  ["/birthday-cruise-vilamoura", "/pt/cruzeiro-aniversario-vilamoura"],
  ["/proposal-cruise-vilamoura", "/pt/cruzeiro-pedido-casamento-vilamoura"],
  ["/boat-trips-quarteira", "/pt/passeios-de-barco-quarteira"],
  ["/boat-trips-albufeira", "/pt/passeios-de-barco-albufeira"],
  ["/boat-trips-faro", "/pt/passeios-de-barco-faro"],
  ["/algarve-boat", "/pt/barco-algarve"],
  ["/boat-hire-algarve", "/pt/aluguer-de-barco-algarve"],
  ["/terms", "/pt/termos"],
  ["/cookie-policy", "/pt/politica-de-cookies"],
  ["/trust-centre", "/pt/centro-de-confianca"],
  ["/destinations", "/pt/destinos"],
];

/** Given the current path, return the equivalent path in the other language. */
export function getAltLangPath(currentPath: string): { en: string; pt: string } {
  const normalized = currentPath.replace(/\/$/, "") || "/";
  for (const [en, pt] of ROUTE_ALT_PAIRS) {
    if (normalized === en) return { en, pt };
    if (normalized === pt) return { en, pt };
  }
  return { en: "/", pt: "/pt" };
}